// import { countUp, untilOrSkip, toDataRow, flipJackpotsAtOnce } from "./chestCollection";
// import { countUp, waitUntil, toDataRow, flipJackpotsAtOnce } from "./chestCollection";
import { countUp, waitUntil, flipJackpotsAtOnce } from "./chestCollection";
import { fireCoinToLogo } from "../Shared/coinToLogoTrail";
import TickerTimeout from "../../utils/TickerTimeout";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import { baseDiag } from "../../utils/fsDiag";
import { isScreenRowOpen } from "../../RGS/Providers/PopiPlay/holdAndWinState";
import { DIM_MIX, SCARAB_LABEL_SCALE } from "./HoldAndWinOverlayCell";
import { paced, trailGapMs } from "./holdAndWinPace";
import { createCoinLabel, setCoinLabel } from "./coinLabel";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import {
  HOLD_AND_WIN_ROWS,
  // toScreenRow, (neighbours are read on screen now)
} from "../../RGS/Providers/PopiPlay/holdAndWinState";

/**
 * scarabReveal — the Scarabs revealing at settlement (GDD "Scarab", End of
 * Hold & Win step 2), before the End Collector collects.
 *
 * In the server's reveal order (storage.scarabs), one Scarab at a time:
 *
 *   the scarab   plays `win` and its "×N" pops in over it — and stays
 *   its targets  every valued neighbour (sides and diagonals, active rows
 *                only) takes the multiplier:
 *                  coin          plays `win`, its number counts up
 *                  jackpot coin  plays `double_value` — the tier word goes,
 *                                the multiplied number pops in, tier colours
 *                  chest         its number counts up
 *                Two scarabs on one cell multiply together, so a later reveal
 *                counts on from the earlier one's result.
 *
 * Before a scarab works, every coin, jackpot coin and chest it does NOT
 * multiply dims (their idles' `_dark` twins — PO, 2026-10-09) and the scarab
 * starts once that has blended in, so its area stands out; the next scarab
 * re-picks the set, and all undim at the end.
 *
 * Each step waits on the spines' own completion — no timers. A press skips
 * what is left: every cell lands on its final number at once.
 *
 * ── Neighbours are the SERVER's ──
 * Adjacency is read in DATA rows (the server's, ±1 inside the active zone)
 * and drawn on the screen rows those map to (contract §14.1) — so the board
 * shows exactly what the server multiplied.
 *
 * ── The number is the server's ──
 * The walk shows the steps; the end state is values × storage.scarab_
 * multipliers, the product grid the payout was computed from — never a
 * client recount. Returns that grid, for the End Collector to collect.
 */

// The 8 neighbours CLOCKWISE from the top-left, [dColumn, dScreenRow] — the
// order "clockwise" mode multiplies them in (FEATURES_CONFIG.SCARAB_REVEAL).
const CLOCKWISE = [
  [-1, -1], [0, -1], [1, -1],
  [1, 0],
  [1, 1], [0, 1], [-1, 1],
  [-1, 0],
];

// LEFT TO RIGHT, the flow of the whole game (Vladimir, 2026-10-06:
// "mandatory"): the column on its left first, then above / below it, then
// the column on its right — top to bottom inside each column.
const LEFT_TO_RIGHT = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1], [0, 1],
  [1, -1], [1, 0], [1, 1],
];

// CLOCKWISE FROM 9 O'CLOCK (Vladimir, 2026-10-07: "left symbol on 9 o'clock,
// then 11 o'clock, 12, 1, 3 and so on"): left, top-left, top, top-right,
// right, bottom-right, bottom, bottom-left — mode "clockwise_from_left".
const CLOCKWISE_FROM_LEFT = [
  [-1, 0], [-1, -1], [0, -1], [1, -1],
  [1, 0], [1, 1], [0, 1], [-1, 1],
];

// The rigs dimmed while a scarab works elsewhere (cell.setDimmed).
const DIMMABLE_RIGS = new Set(["c", "cj", "ch"]);

// A multiplied coin's number counting up, ms (the chests' and collector's).
const COUNT_MS = 150;

// The longest one reveal step may take before the round moves on without it,
// ms — only for a clip that never reports its end (a step is ~0.7 s).
const STEP_SAFETY_MS = 4000;

// ── The scarab's shot (2026-10-15, PO) ── for each cell it multiplies, the
// scarab sends a fireball; the cell pulses and counts as it arrives. Its own
// look against every other trail: FLAT (straight at the coin), SHORTER (a
// quick flight, a short tail), LIGHTER (fewer particles and tail flames) and
// RED (pale red head burning out to deep red, red-tinted flames).
const SCARAB_SHOT = {
  // flat: true,
  // Thinner (2026-10-15: "fireball diameter needs to be smaller") and bowed
  // to the LEFT of its direction, always.
  // sizeScale: 0.6,
  sizeScale: 0.72, // slightly thicker (2026-10-15)
  bowLeft: true,
  // A slight curve back (2026-10-15: "flat is just not vibrant enough") —
  // see SCARAB_SHOT_ARC.
  // durationMs: 220,
  // durationMs: 260, // a bit longer (2026-10-15)
  // Easier to see (2026-10-15: "i kinda cant see it"): additive, so a dark red
  // added over the orange coins barely showed — a hot pink-white head burning
  // out to red reads as a red fireball; a touch denser and slower too.
  durationMs: 320,
  // spawnCount: 8,
  spawnCount: 12,
  // particleLife: 0.16,
  // particleLife: 0.22,
  // particleLife: 0.28, // a longer tail (2026-10-15)
  // The tail spans the WHOLE flight as it lands (2026-10-15: "reaches full
  // trail from scarab to symbol") — it outlives the 320 ms flight.
  particleLife: 0.4,
  // flameSpawnMs: 55,
  flameSpawnMs: 40,
  // flameLifeMs: 160,
  // flameLifeMs: 220,
  // flameLifeMs: 280,
  flameLifeMs: 380,
  // tintStart: 0xffb39a,
  // tintStart: 0xffe6e0,
  // (no red tint — 2026-10-15: it blended into the art; the standard fire colours)
  // tintEnd: 0xc4102a,
  // tintEnd: 0xff2a1a,
  // flameTint: 0xff6a5a,
  // flameTint: 0xff8070,
};
// The same flight with the fire off — no particles, no tail flames — for the
// letters it carries (see shoot).
const SCARAB_LETTERS = { ...SCARAB_SHOT, fire: false };
// Its bow: half the H&W trails' (chestCollection HNW_TRAIL_ARC, 0.3) — off the
// middle of the line, this share of the distance.
// const SCARAB_SHOT_ARC = { height: 0.15, lift: 0 };
// The same curve as the H&W trails (2026-10-15: "use same curve … same as
// the rest") — chestCollection's HNW_TRAIL_ARC.
const SCARAB_SHOT_ARC = { height: 0.3, lift: 0.05 };
// ── The next shot when the number is DONE multiplying (2026-10-15) ── the
// coin's number pulses inside its `win` (c / cj.json: txt_slot_coin 1 -> 1.4
// at 0.2 s -> back to 1 at 0.667 s); the next neighbour's shot leaves then.
// The chest has no number pulse of its own and takes the same beat.
const NUMBER_DONE_MS = 667;
// // ── A flowing reveal (2026-10-15) ── REVERTED (shots all at once).
// const SHOT_STAGGER_MS = 140;
// The longest a hit waits for its shot to land.
const SHOT_SAFETY_MS = 800;

/** The grid each cell ends on: value × the server's product for that cell. */
const finalGrid = (values, multipliers) =>
  values.map((column, c) =>
    column.map((v, r) => {
      const m = multipliers?.[c]?.[r];
      return v > 0 && m > 1 ? Math.round(v * m) : v;
    }),
  );

/**
 * @param {object}   p
 * @param {Array}    p.scarabs           holdAndWin.step.scarabs — reveal order
 * @param {Array}    p.values            the server's grid, screen rows
 * @param {Array}    [p.scarabMultipliers] the server's product grid, screen rows
 * @param {number}   p.unlockedRows      active rows (data rows 0..n-1)
 * @param {object}   p.overlay           HoldAndWinOverlay's handle
 * @param {Function} p.isSkipped         true once the player pressed to skip
 * @returns {Promise<Array>} the multiplied grid (screen rows)
 */
export const playScarabReveal = async ({
  scarabs,
  values,
  scarabMultipliers,
  unlockedRows,
  overlay,
  isSkipped,
}) => {
  if (!values) return values;
  const target = finalGrid(values, scarabMultipliers);
  // What the reveal was handed, against what stands on the board: a scarab
  // on the board with no entry is one the server did not list (the simulator
  // leaves out a scarab with no valued neighbour — the GDD says it still
  // reveals).
  let onBoard = 0;
  for (let c = 0; c < values.length; c++) {
    for (let r = 0; r < values[c].length; r++) {
      if (overlay?.rig?.(c, r) === "scr") onBoard += 1;
    }
  }
  baseDiag("H&W scarab reveal", {
    listed: Array.isArray(scarabs) ? scarabs.length : null,
    onBoard,
  });
  if (!Array.isArray(scarabs) || !scarabs.length || !overlay) return target;

  const running = values.map((column) => column.slice());
  const product = values.map((column) => column.map(() => 1));
  const columns = values.length;

  // ── Jackpot coins a scarab will hit flip FIRST, all at once ──
  // (Vladimir, 2026-10-06.) Each shows its own value before any scarab plays;
  // the walk then multiplies it like any other coin. Turning one inside the
  // walk replayed `double_value` on a coin already showing a number. Its
  // valued neighbours as the walk reads them (server rows, active zone).
  const neighbourValues = (reel, screenRow) => {
    const out = [];
    // const dataRow = toDataRow(screenRow); (neighbours are read on screen now)
    for (let dc = -1; dc <= 1; dc++) {
      for (let dr = -1; dr <= 1; dr++) {
        if (!dc && !dr) continue;
        const c = reel + dc;
        // const d = dataRow + dr;
        // if (c < 0 || c >= columns || d < 0 || d >= unlockedRows || d >= HOLD_AND_WIN_ROWS) {
        //   continue;
        // }
        // const r = toScreenRow(d);
        // ── Neighbours on SCREEN (GDD: the 8 cells around it) ── see the walk.
        const r = screenRow + dr;
        if (c < 0 || c >= columns || r < 0 || r >= HOLD_AND_WIN_ROWS) continue;
        // if (toDataRow(r) >= unlockedRows) continue;
        if (!isScreenRowOpen(r, unlockedRows)) continue;
        if (values[c]?.[r] > 0) out.push({ c, r, v: values[c][r] });
      }
    }
    return out;
  };
  await flipJackpotsAtOnce(
    overlay,
    scarabs.flatMap(({ reel, screenRow }) => neighbourValues(reel, screenRow)),
  );

  // // Scarabs already dark (each after its own multiply) — see the end.
  // const darkened = new Set();
  // for (const { reel, screenRow, multiplier } of scarabs) {
  //   if (isSkipped()) break;
  //
  //   // The scarab: `win`, and its ×N.
  //   // await untilOrSkip(overlay.revealScarab(reel, screenRow, multiplier), isSkipped);
  //   // if (isSkipped()) break;
  //   // ── At the same time as its neighbours ── the scarab's `win` is its
  //   // glow; the multiplying starts with it, not after it (Vladimir,
  //   // 2026-09-30: "it should happen all at once").
  //   // const scarabWin = overlay.revealScarab(reel, screenRow, multiplier);
  //   // ── Two modes (2026-10-06) ── "all_at_once": its glow and every
  //   // neighbour together (as before). "clockwise": one glow per neighbour.
  //   // Each neighbour is a TASK, started when its turn comes.
  //   // const clockwise = FEATURES_CONFIG.SCARAB_REVEAL?.MODE === "clockwise";
  //   // One glow per neighbour, in the mode's order ("left_to_right" or
  //   // "clockwise"); "all_at_once" keeps the single glow.
  //   const mode = FEATURES_CONFIG.SCARAB_REVEAL?.MODE ?? "left_to_right";
  //   const clockwise = mode === "clockwise" || mode === "left_to_right";
  //   const order = mode === "clockwise" ? CLOCKWISE : LEFT_TO_RIGHT;
  //   // On top of everything it multiplies while it works (other scarabs too).
  //   overlay.setRevealing?.(reel, screenRow, true);
  //
  //   // Its valued neighbours, in the server's rows.
  //   // const dataRow = toDataRow(screenRow); (neighbours are read on screen now)
  //   const hits = [];
  //   // for (let dc = -1; dc <= 1; dc++) {
  //   //   for (let dr = -1; dr <= 1; dr++) {
  //   //     if (!dc && !dr) continue;
  //   // for (const [dc, dr] of CLOCKWISE) {
  //   for (const [dc, dr] of order) {
  //     const c = reel + dc;
  //     // const d = dataRow + dr;
  //     // if (c < 0 || c >= columns || d < 0 || d >= unlockedRows || d >= HOLD_AND_WIN_ROWS) {
  //     //   continue;
  //     // }
  //     // const r = toScreenRow(d);
  //     // ── Neighbours on SCREEN (GDD: the 8 cells around it, 2026-10-06) ──
  //     // Data rows ±1 joined the bottom row to the row opened above the zone
  //     // (far apart on screen) and split the zone's top row from it
  //     // (touching). The simulator now reads them on screen too
  //     // (dmon-rgsim screenRowOf); active zone only.
  //     const r = screenRow + dr;
  //     if (c < 0 || c >= columns || r < 0 || r >= HOLD_AND_WIN_ROWS) continue;
  //     if (toDataRow(r) >= unlockedRows) continue;
  //     const base = values[c]?.[r];
  //     if (!(base > 0)) continue;
  //     product[c][r] *= multiplier;
  //     const from = running[c][r];
  //     const to = Math.round(base * product[c][r]);
  //     running[c][r] = to;
  //     const rig = overlay.rig(c, r);
  //     // if (rig === "cj") {
  //     //   hits.push(overlay.revealJackpotValue(c, r, to));
  //     // } else if (rig === "c") {
  //     // A jackpot coin still on its word (it was flipped above, so only if
  //     // that failed) turns here; a turned one multiplies like a coin.
  //     if (rig === "cj" && overlay.isHiddenJackpot?.(c, r)) {
  //       hits.push(() => overlay.revealJackpotValue(c, r, to));
  //     } else if (rig === "cj") {
  //       hits.push(() =>
  //         Promise.all([
  //           // overlay.playWin(c, r),
  //           // Its `multiply` clip (the number pulses), as a coin's.
  //           overlay.playMultiplied?.(c, r) ?? overlay.playWin(c, r),
  //           countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)),
  //         ]),
  //       );
  //     } else if (rig === "c") {
  //       hits.push(() =>
  //         Promise.all([
  //           // overlay.playWin(c, r),
  //           // Its anticipation clips, quick, instead of `win` (Vladimir,
  //           // 2026-10-01: "its better looking").
  //           overlay.playMultiplied?.(c, r) ?? overlay.playWin(c, r),
  //           countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)),
  //         ]),
  //       );
  //     } else if (rig === "ch") {
  //       // A chest: its `multiply` (body + number), over its open/closed state.
  //       hits.push(() =>
  //         Promise.all([
  //           overlay.playChestMultiplied?.(c, r) ?? Promise.resolve(),
  //           countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)),
  //         ]),
  //       );
  //     } else {
  //       hits.push(() => countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n)));
  //     }
  //   }
  //   baseDiag("H&W scarab", {
  //     cell: `${reel},${screenRow}`,
  //     multiplier,
  //     hits: hits.length,
  //   });
  //   // await untilOrSkip(Promise.all(hits), isSkipped);
  //   // await untilOrSkip(Promise.all([scarabWin, ...hits]), isSkipped);
  //   if (!clockwise || !hits.length) {
  //     const scarabWin = overlay.revealScarab(reel, screenRow, multiplier);
  //     await untilOrSkip(Promise.all([scarabWin, ...hits.map((hit) => hit())]), isSkipped);
  //   } else {
  //     // One glow, one neighbour — its first glow pops the ×N in.
  //     for (let i = 0; i < hits.length; i++) {
  //       // A press: stop here — the final pass below lands every number at
  //       // once, exactly as a skip of the all-at-once reveal does. (Starting
  //       // the rest's count-ups here let them write over the final numbers.)
  //       if (isSkipped()) break;
  //       const glow =
  //         i === 0
  //           ? overlay.revealScarab(reel, screenRow, multiplier)
  //           : overlay.playWin(reel, screenRow);
  //       await untilOrSkip(Promise.all([glow, hits[i]()]), isSkipped);
  //     }
  //   }
  //   // Done its job — it goes dark (2026-10-05, client: like a collected coin).
  //   overlay.darken(reel, screenRow);
  //   darkened.add(`${reel},${screenRow}`);
  //   overlay.setRevealing?.(reel, screenRow, false);
  // }

  // ── A press: the step on screen FINISHES, the rest plays AT ONCE ──
  // (Vladimir, 2026-10-06.) Cutting the step froze the scarab mid-glow (its
  // `darken` cut its `win`) and left the rest snapping to their numbers, with
  // the cut neighbour's count still running under the collection. Now the
  // glow + multiply already started play out in full; then every scarab left
  // glows together and every neighbour left multiplies once, straight to the
  // server's number. Nothing is cut or replayed; a second press does nothing
  // (the press flag stays up until the reveal is over).
  // const mode = FEATURES_CONFIG.SCARAB_REVEAL?.MODE ?? "left_to_right";
  const mode = FEATURES_CONFIG.SCARAB_REVEAL?.MODE ?? "clockwise_from_left";
  // One glow per neighbour ("left_to_right" / "clockwise"), or one for all.
  // const oneByOne = mode === "clockwise" || mode === "left_to_right";
  // const order = mode === "clockwise" ? CLOCKWISE : LEFT_TO_RIGHT;
  // One glow per neighbour in every mode but "all_at_once".
  const oneByOne = mode !== "all_at_once";
  const ORDER_BY_MODE = {
    clockwise: CLOCKWISE,
    left_to_right: LEFT_TO_RIGHT,
    clockwise_from_left: CLOCKWISE_FROM_LEFT,
  };
  const order = ORDER_BY_MODE[mode] ?? CLOCKWISE_FROM_LEFT;

  // The plan: each scarab's valued neighbours, in order, with the number each
  // one counts from and to (two scarabs on one cell multiply together).
  // const plan = scarabs.map(({ reel, screenRow, multiplier }) => {
  const fullPlan = scarabs.map(({ reel, screenRow, multiplier }) => {
    const hits = [];
    for (const [dc, dr] of order) {
      const c = reel + dc;
      // Neighbours on SCREEN, active zone only (see neighbourValues).
      const r = screenRow + dr;
      if (c < 0 || c >= columns || r < 0 || r >= HOLD_AND_WIN_ROWS) continue;
      // if (toDataRow(r) >= unlockedRows) continue;
      if (!isScreenRowOpen(r, unlockedRows)) continue;
      const base = values[c]?.[r];
      if (!(base > 0)) continue;
      product[c][r] *= multiplier;
      const from = running[c][r];
      const to = Math.round(base * product[c][r]);
      running[c][r] = to;
      hits.push({ c, r, from, to });
    }
    return { reel, screenRow, multiplier, hits };
  });
  // ── A scarab with nothing to multiply does NOTHING (2026-10-19, PO) ── no
  // dim, no glow, no ×N: it played a glow at nothing. It keeps its place in
  // the walk and only goes dark when its turn comes, like the others.
  // const plan = fullPlan.filter(({ hits }) => hits.length > 0);
  const plan = fullPlan;
  const idle = new Set(
    fullPlan.filter(({ hits }) => !hits.length).map(({ reel, screenRow }) => `${reel},${screenRow}`),
  );

  // One neighbour taking the multiplier: its clip + its number counting.
  // const playHit = ({ c, r, from, to }) => {
  // Over every cell around it while it takes the hit — only the working
  // scarab above it (cell HIT_Z, 2026-10-15).
  const playHit = (hit) => {
    overlay.setHit?.(hit.c, hit.r, true);
    return Promise.resolve(playHitClip(hit)).finally(() => overlay.setHit?.(hit.c, hit.r, false));
  };
  // The scarab's fireball into the cell, then its hit (SCARAB_SHOT). A shot
  // that cannot fly (no cell, no layer) or never lands goes straight on.
  const shoot = (scarab, hit) =>
    new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        safety.clear();
        resolve();
      };
      const safety = new TickerTimeout(finish, SHOT_SAFETY_MS);
      const from = overlay.cellAnchor?.(scarab.reel, scarab.screenRow);
      const to = overlay.cellAnchor?.(hit.c, hit.r);
      const container = overlay.trailLayer?.();
      if (!from || !to || !container || from.destroyed || to.destroyed) {
        finish();
        return;
      }
      // The scarab's multiplier flies, not a fireball (Vladimir, 2026-10-22:
      // "same trail but letters fly instead of fire") — a copy of its own
      // "N×" label, at its size on screen, on the trail's head; the fire is off.
      const carry = createCoinLabel();
      const wt = container.worldTransform;
      const worldScale = Math.hypot(wt.a, wt.b) || 1;
      const cellUnits = (overlay.cellHeight?.() || Settings.SYMBOL_HEIGHT) / worldScale;
      setCoinLabel(
        carry,
        `${scarab.multiplier}×`,
        cellUnits * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER * SCARAB_LABEL_SCALE,
        cellUnits * 0.9,
      );
      fireCoinToLogo({
        layer: {
          trailContainer: container,
          get symbolHeight() {
            return overlay.cellHeight?.();
          },
        },
        from,
        target: to.toGlobal({ x: 0, y: 0 }),
        onArrival: finish,
        // style: SCARAB_SHOT,
        style: SCARAB_LETTERS,
        // // At the respin's pace — turbo's shot is quicker (holdAndWinPace, 2026-10-23).
        // style: { ...SCARAB_LETTERS, durationMs: paced(SCARAB_LETTERS.durationMs) },
        // (Reverted 2026-10-23: the shot always flies at its own speed.)
        carry,
        arc: SCARAB_SHOT_ARC,
        // Silent: the scarab is heard by its own scarab_activate on each hit
        // (2026-10-22: "dont make them 2").
        sound: null,
      });
    });
  // const strike = (scarab, hit) => shoot(scarab, hit).then(() => playHit(hit));
  // ...heard as it hits, as the one-by-one shots are (2026-10-23: the skip /
  // super turbo burst was silent) — once per frame, so a burst is one sound.
  const strike = (scarab, hit) =>
    shoot(scarab, hit).then(() => {
      soundManager.playOncePerFrame(SOUND_NAME.scarab_activate);
      return playHit(hit);
    });
  const playHitClip = ({ c, r, from, to }) => {
    const rig = overlay.rig(c, r);
    // A jackpot coin still on its word (the flip above failed) turns here.
    // if (rig === "cj" && overlay.isHiddenJackpot?.(c, r)) {
    //   return overlay.revealJackpotValue(c, r, to);
    // }
    // ...with the scarab's glow behind it, like every coin a scarab hits
    // (2026-10-12) — after the reveal has started (it clears the tracks).
    if (rig === "cj" && overlay.isHiddenJackpot?.(c, r)) {
      const reveal = overlay.revealJackpotValue(c, r, to);
      overlay.playMultiplyGlow?.(c, r);
      return reveal;
    }
    // const count = countUp(from, to, COUNT_MS, (n) => overlay.setValue(c, r, n));
    const count = countUp(from, to, paced(COUNT_MS), (n) => overlay.setValue(c, r, n));
    if (rig === "cj" || rig === "c") {
      // Its `multiply` clip (the number pulses).
      return Promise.all([overlay.playMultiplied?.(c, r) ?? overlay.playWin(c, r), count]);
    }
    if (rig === "ch") {
      // A chest: its `multiply`, over its open/closed state.
      return Promise.all([overlay.playChestMultiplied?.(c, r) ?? Promise.resolve(), count]);
    }
    return count;
  };

  // A step is awaited in FULL — never cut by a press. A clip that never
  // reports back cannot hold the round: the ticker lets it go after this.
  const playOut = (promise) => {
    let done = false;
    Promise.resolve(promise).then(() => {
      done = true;
    });
    return waitUntil(() => done, STEP_SAFETY_MS);
  };

  // The number each cell shows (a started count always reaches its `to`).
  const shown = values.map((column) => column.slice());
  const keyOf = (c, r) => `${c},${r}`;
  // Scarabs whose ×N has popped in, and those already dark.
  const revealed = new Set();
  const darkened = new Set();
  const glowOf = ({ reel, screenRow, multiplier }) => {
    const key = keyOf(reel, screenRow);
    if (revealed.has(key)) return overlay.playWin(reel, screenRow);
    revealed.add(key);
    // // The scarab going to work — its ×N popping in (batch 3, 2026-10-22).
    // soundManager.playOncePerFrame(SOUND_NAME.scarab_activate);
    return overlay.revealScarab(reel, screenRow, multiplier);
  };
  const finish = ({ reel, screenRow }) => {
    // Once (a press can reach it from the flowing reveal and the skip both).
    if (darkened.has(keyOf(reel, screenRow))) return;
    // Done its job — it goes dark (2026-10-05, client: like a collected coin).
    overlay.darken(reel, screenRow);
    darkened.add(keyOf(reel, screenRow));
    overlay.setRevealing?.(reel, screenRow, false);
  };

  // ── Dim what the working scarab does not multiply ──
  const dimmable = [];
  for (let c = 0; c < columns; c++) {
    for (let r = 0; r < values[c].length; r++) {
      if (DIMMABLE_RIGS.has(overlay.rig(c, r))) dimmable.push({ c, r });
    }
  }
  // Dims (and lights) the set, then waits for the blend — only if a cell
  // actually changed.
  const dimAllBut = async (hits) => {
    const lit = new Set(hits.map(({ c, r }) => keyOf(c, r)));
    let changed = false;
    for (const { c, r } of dimmable) {
      if (overlay.setDimmed?.(c, r, !lit.has(keyOf(c, r)))) changed = true;
    }
    if (changed) await waitUntil(() => false, DIM_MIX * 1000);
  };
  const undimAll = () => {
    for (const { c, r } of dimmable) overlay.setDimmed?.(c, r, false);
  };

  // Where a press landed: scarab `s`, its first neighbour not yet started `h`.
  let resume = null;
  // Each scarab's glow, hits and darkening still playing — awaited at the end.
  const pending = [];
  for (let s = 0; s < plan.length && !resume; s++) {
    const scarab = plan[s];
    if (isSkipped()) {
      resume = { s, h: 0 };
      break;
    }
    // Nothing to multiply: dark on its turn, nothing else (see `idle`).
    if (!scarab.hits.length) {
      finish(scarab);
      continue;
    }
    // On top of everything it multiplies while it works (other scarabs too).
    overlay.setRevealing?.(scarab.reel, scarab.screenRow, true);
    await dimAllBut(scarab.hits);
    baseDiag("H&W scarab", {
      cell: keyOf(scarab.reel, scarab.screenRow),
      multiplier: scarab.multiplier,
      hits: scarab.hits.length,
    });
    // One glow per neighbour, or one glow with all of them.
    const steps =
      oneByOne && scarab.hits.length ? scarab.hits.map((hit) => [hit]) : [scarab.hits];
    let h = 0;
    // for (const step of steps) {
    //   if (isSkipped()) {
    //     resume = { s, h };
    //     break;
    //   }
    //   for (const hit of step) shown[hit.c][hit.r] = hit.to;
    //   // await playOut(Promise.all([glowOf(scarab), ...step.map(playHit)]));
    //   await playOut(Promise.all([glowOf(scarab), ...step.map((hit) => strike(scarab, hit))]));
    //   h += step.length;
    // }
    // if (!resume) finish(scarab);
    // // The scarab glows once; its shots leave SHOT_STAGGER_MS apart, each hit
    // // on its shot's landing; it goes dark when its hits are done — on its own.
    // (REVERTED 2026-10-15: "all are shooting at once … you changed the whole
    // concept" — one shot at a time, one scarab at a time, as before.)
    // ── The same order, without the drag (2026-10-15) ── the scarab glows
    // ONCE (its `win` replayed in full before every neighbour); the next
    // neighbour's shot leaves once this one has landed and counted up
    // (COUNT_MS) — its pulse finishes meanwhile. The next scarab still waits
    // for every hit and this one's darkening.
    // const running = [glowOf(scarab)];
    // The scarab glows with EVERY shot again (2026-10-15: "why isn't scarab
    // glowing on every shot") — not waited on: the shot's beat sets the pace.
    const running = [];
    // ── Turbo: the shots one by one with the trails' gap (2026-10-23: "scarab
    // should have same gaps, not at once on turbo") ── each leaves
    // TRAIL_GAP_MS after the one before, not waiting for it to land; at its
    // own speed. null = normal: each waits for the last one's number.
    const gap = trailGapMs();
    const shots = [];
    for (const step of steps) {
      if (isSkipped()) {
        resume = { s, h };
        break;
      }
      running.push(glowOf(scarab));
      for (const hit of step) shown[hit.c][hit.r] = hit.to;
      // await playOut(
      const stepDone = Promise.all(
        step.map((hit) =>
          shoot(scarab, hit).then(() => {
            // The scarab multiplying this neighbour (batch 3, 2026-10-22) —
            // once per frame, so hits landing together are one sound.
            soundManager.playOncePerFrame(SOUND_NAME.scarab_activate);
            running.push(playHit(hit));
            // return waitUntil(() => false, COUNT_MS);
            // return waitUntil(() => false, NUMBER_DONE_MS);
            // return waitUntil(() => false, paced(NUMBER_DONE_MS));
            return gap == null ? waitUntil(() => false, paced(NUMBER_DONE_MS)) : null;
          }),
        ),
      );
      if (gap == null) {
        await playOut(stepDone);
      } else {
        shots.push(stepDone);
        await waitUntil(isSkipped, gap);
      }
      h += step.length;
    }
    // Turbo: every shot of this scarab landed before the next scarab.
    if (shots.length) await playOut(Promise.all(shots));
    // await playOut(Promise.all(running));
    // if (!resume) finish(scarab);
    // ── The next scarab at once (2026-10-15: "new scarab waits for old one to
    // go dark … let previous one darken on its own") ── this one finishes its
    // glow and darkens in the background; all of it is awaited at the end.
    const pressedHere = !!resume;
    pending.push(
      playOut(Promise.all(running)).then(() => {
        if (!pressedHere) finish(scarab);
      }),
    );
  }
  // Every scarab's hits and darkening played out before anything moves on.
  await Promise.all(pending);

  // ── The rest, AT ONCE ── every scarab left glows (its ×N pops in if it
  // had not yet), every neighbour left multiplies once to its final number.
  if (resume) {
    const left = plan.slice(resume.s);
    const cells = new Map();
    // left.forEach(({ hits }, i) => {
    //   for (const { c, r } of hits.slice(i === 0 ? resume.h : 0)) {
    //     const key = keyOf(c, r);
    //     if (!cells.has(key)) cells.set(key, { c, r, from: shown[c][r], to: target[c][r] });
    //   }
    // });
    // ...each with the scarab that shoots it (its first one left).
    left.forEach((scarab, i) => {
      for (const { c, r } of scarab.hits.slice(i === 0 ? resume.h : 0)) {
        const key = keyOf(c, r);
        if (!cells.has(key)) {
          cells.set(key, { c, r, from: shown[c][r], to: target[c][r], scarab });
        }
      }
    });
    baseDiag("H&W scarab skip", { scarabsLeft: left.length, cellsLeft: cells.size });
    // for (const { reel, screenRow } of left) overlay.setRevealing?.(reel, screenRow, true);
    // Only the scarabs with work glow; an idle one just goes dark below.
    const working = left.filter(({ hits }) => hits.length > 0);
    for (const { reel, screenRow } of working) overlay.setRevealing?.(reel, screenRow, true);
    await dimAllBut([...cells.values()]);
    // const glows = left.map(glowOf);
    const glows = working.map(glowOf);
    for (const cell of cells.values()) shown[cell.c][cell.r] = cell.to;
    // await playOut(Promise.all([...glows, ...[...cells.values()].map(playHit)]));
    await playOut(
      Promise.all([...glows, ...[...cells.values()].map((cell) => strike(cell.scarab, cell))]),
    );
    left.forEach(finish);
  }

  undimAll();
  // No cell keeps the hit lift (a clip that never reported back).
  for (const { hits } of plan) for (const { c, r } of hits) overlay.setHit?.(c, r, false);

  // ── The server's numbers, always ── (and every step a press skipped)
  for (let c = 0; c < columns; c++) {
    for (let r = 0; r < target[c].length; r++) {
      if (target[c][r] === values[c][r]) continue;
      // if (overlay.rig(c, r) === "cj") {
      // A jackpot coin still on its word turns now; a turned one only takes
      // its number — never `double_value` twice (2026-10-06).
      if (overlay.rig(c, r) === "cj" && overlay.isHiddenJackpot?.(c, r)) {
        // A jackpot coin not yet turned (a press skipped it) turns now.
        if (running[c][r] !== target[c][r]) {
          overlay.revealJackpotValue(c, r, target[c][r]);
        }
      } else {
        overlay.setValue(c, r, target[c][r]);
      }
    }
  }
  const sum = (grid) =>
    grid.reduce((t, column) => t + column.reduce((a, v) => a + (v > 0 ? v : 0), 0), 0);
  baseDiag("H&W scarab reveal done", { before: sum(values), after: sum(target) });
  // Scarabs a press skipped still show their ×N.
  for (const { reel, screenRow, multiplier } of scarabs) {
    // An idle scarab: no ×N — it only goes dark (normally done on its turn).
    if (idle.has(`${reel},${screenRow}`)) {
      if (!darkened.has(`${reel},${screenRow}`)) overlay.darken(reel, screenRow);
      continue;
    }
    overlay.showScarabMultiplier?.(reel, screenRow, multiplier);
    // ...and go dark, once (2026-10-05).
    if (!darkened.has(`${reel},${screenRow}`)) overlay.darken(reel, screenRow);
  }
  return target;
};
